import Resource from './Resource';
import AppService from 'App/legacy/AppService';
import parseCustom from 'App/resources/parsers/custom';
import type CustomFieldModel from 'App/resources/Model/CustomField';

/*
	Standalone CustomField (WEB-7780). Ports the two read members CacheRefresher needs off the
	Angular factory in upsales/common/resources/customField.js. Everything else on that factory
	(save/delete/getCached/findIdMap/…) still has Angular callers and stays there.

	Faithfulness notes:

	- Angular declared `transformResponse: ParseGeneric('ParseCustom', { isArray: true,
	  skipDateConvert: true })`, which is exactly `res.data = res.data.map(parseCustom)` — no date
	  conversion and no custom-field expansion (`opts.custom` is falsy). `Resource` reproduces that
	  only when no attributes are passed to `super()`: `_parse` then runs `parseDates(field, [])`
	  (a no-op with the default empty `dateFields`), skips `genericParser`, and calls `parse`.
	  Passing an attributes object here would silently start running `genericParser` over every
	  field and change the payload.
	- `ParseCustom` is a one-line Angular wrapper around `App/resources/parsers/custom`, so the
	  parser below is literally the same function the $resource used.
	- `find({})` collapses to `params: undefined` in `Resource.find`, so no query string is sent —
	  matching Angular, whose params were consumed entirely by the URL template.
	- `_url` has no trailing slash: `_getUrl` appends the (empty) path with a `/`, producing
	  `.../customfields/{type}/`. The API routes are `/customfields/{type}/?$` and
	  `/customfields/userDefinedObject/:nr/?$`, so the trailing slash is accepted. (Angular's
	  $resource stripped it; one optional slash is the only difference in the request.)
*/
class CustomFieldResource extends Resource {
	constructor() {
		super('customfields');
	}

	parse(field: CustomFieldModel): CustomFieldModel {
		return parseCustom(field);
	}

	findByType(type: string): Promise<CustomFieldModel[]> {
		const customerId = AppService.getCustomerId();
		return super
			.find({}, { _url: `${customerId}/customfields/${type}` })
			.then((res: { data: CustomFieldModel[] }) => res.data);
	}

	findUdo(nr: number): Promise<CustomFieldModel[]> {
		const customerId = AppService.getCustomerId();
		return super
			.find({}, { _url: `${customerId}/customfields/userDefinedObject/${nr}` })
			.then((res: { data: CustomFieldModel[] }) => res.data);
	}
}

export default new CustomFieldResource();
